import { SpreadsheetFormatActions } from '../const/ActionsConst';
import { loadIndividualSpreadsheetFormat, loadPedigreeSpreadsheetFormat } from '@common/actions/table';
import { SPREADSHEET_TYPE } from '../const/common';

/**
 * Action creator to store format data for a spreadsheet ID
 * @param {number} spreadsheetId - The spreadsheet ID
 * @param {string} spreadsheetType - The spreadsheet type ("Individual" or "Pedigree")
 * @param {Object} formatData - The format data object containing:
 *   - format: { id, name, description, folderId }
 *   - columnModel: { smartColumns: [...] }
 *   - queryModel: { whereClauses: [...] }
 *   - timestamp: number (optional, will be set automatically if not provided)
 * @returns {Object} Redux action
 */
export const storeFormatData = (spreadsheetId, spreadsheetType, formatData) => {
    if (spreadsheetId == null || spreadsheetId === undefined) {
        throw new Error('spreadsheetId is required');
    }
    if (!spreadsheetType) {
        throw new Error('spreadsheetType is required');
    }
    
    return {
        type: SpreadsheetFormatActions.STORE_FORMAT_DATA,
        payload: {
            spreadsheetId: Number(spreadsheetId),
            spreadsheetType: spreadsheetType,
            formatData: {
                ...formatData,
                timestamp: formatData.timestamp || Date.now()
            }
        }
    };
};

/**
 * Action creator to update format data for a spreadsheet ID
 * @param {number} spreadsheetId - The spreadsheet ID
 * @param {string} spreadsheetType - The spreadsheet type ("Individual" or "Pedigree")
 * @param {Object} formatData - Partial format data to update
 * @returns {Object} Redux action
 */
export const updateFormatData = (spreadsheetId, spreadsheetType, formatData) => {
    if (spreadsheetId == null || spreadsheetId === undefined) {
        throw new Error('spreadsheetId is required');
    }
    if (!spreadsheetType) {
        throw new Error('spreadsheetType is required');
    }
    
    return {
        type: SpreadsheetFormatActions.UPDATE_FORMAT_DATA,
        payload: {
            spreadsheetId: Number(spreadsheetId),
            spreadsheetType: spreadsheetType,
            formatData
        }
    };
};

/**
 * Action creator to clear format data for a specific spreadsheet ID
 * @param {number} spreadsheetId - The spreadsheet ID to clear
 * @returns {Object} Redux action
 */
export const clearFormatData = (spreadsheetId, spreadsheetType) => {
    if (spreadsheetId == null || spreadsheetId === undefined) {
        throw new Error('spreadsheetId is required');
    }
    if (!spreadsheetType) {
        throw new Error('spreadsheetType is required');
    }
    
    return {
        type: SpreadsheetFormatActions.CLEAR_FORMAT_DATA,
        payload: {
            spreadsheetId: Number(spreadsheetId),
            spreadsheetType: spreadsheetType
        }
    };
};

/**
 * Action creator to clear all format data
 * @returns {Object} Redux action
 */
export const clearAllFormatData = () => ({
    type: SpreadsheetFormatActions.CLEAR_ALL_FORMAT_DATA
});

/**
 * Thunk action to load and store format data from API
 * This replaces the sessionStorage pattern with Redux
 * @param {number} spreadsheetType - The spreadsheet type (SPREADSHEET_TYPE.individual or SPREADSHEET_TYPE.pedigree)
 * @param {number} spreadsheetId - The spreadsheet ID
 * @param {string} name - The spreadsheet name (optional)
 * @returns {Function} Redux thunk action
 */
export const loadAndStoreFormatData = (spreadsheetType, spreadsheetId, name = null) => {
    return async (dispatch) => {
        try {
            // Determine which API function to use based on spreadsheet type
            const loadFormatFn = spreadsheetType === SPREADSHEET_TYPE.individual
                ? loadIndividualSpreadsheetFormat
                : loadPedigreeSpreadsheetFormat;

            // Load format data from API
            const formatData = await loadFormatFn(Number(spreadsheetId));
            
            if (!formatData) {
                throw new Error('Failed to load format data');
            }

            const columnModel = formatData?.columnModel || formatData || { smartColumns: [] };
            const queryModel = formatData?.queryModel || { whereClauses: [] };

            // Filter out dummy/system columns such as row count and invalid ids
            const filteredSmartColumns = (columnModel.smartColumns || []).filter((col) => {
                const fid = col?.field?.fieldid;
                const isDummy = fid === -599 || fid === -500;
                const isRowCount = fid === 999999; // fallback; actual constant handled in builder
                const isInvalid = !fid || fid <= 0;
                const isHiddenOrder = col?.columnOrder === -1;
                return !(isDummy || isRowCount || isInvalid || isHiddenOrder);
            });

            // Prepare format data to return (not stored in Redux - format storage is disabled)
            const formatDataToStore = {
                format: {
                    id: Number(spreadsheetId),
                    name: name || formatData?.formatDetails?.name || '',
                    description: formatData?.formatDetails?.description || '',
                    folderId: formatData?.formatDetails?.folderId || 0
                },
                columnModel: {
                    smartColumns: filteredSmartColumns
                },
                queryModel,
                timestamp: Date.now()
            };

            // Format data is not stored in Redux - focus is on filter storage only
            // Return the data for use by caller if needed

            return formatDataToStore;
        } catch (error) {
            console.error('Error loading and storing format data:', error);
            throw error;
        }
    };
};

/**
 * Action creator to store folder data for a spreadsheet ID
 * @param {number} spreadsheetId - The spreadsheet ID
 * @param {string} spreadsheetType - The spreadsheet type ("Individual" or "Pedigree")
 * @param {Object} folder - The folder data (FolderPrimaryListDto)
 * @returns {Object} Redux action
 */
export const storeFolderData = (spreadsheetId, spreadsheetType, folder) => {
    if (spreadsheetId == null || spreadsheetId === undefined) {
        throw new Error('spreadsheetId is required');
    }
    if (!spreadsheetType) {
        throw new Error('spreadsheetType is required');
    }
    
    return {
        type: SpreadsheetFormatActions.STORE_FOLDER_DATA,
        payload: {
            spreadsheetId: Number(spreadsheetId),
            spreadsheetType: spreadsheetType,
            folder
        }
    };
};

/**
 * Action creator to update folder data for a spreadsheet ID
 * @param {number} spreadsheetId - The spreadsheet ID
 * @param {string} spreadsheetType - The spreadsheet type ("Individual" or "Pedigree")
 * @param {Object} folder - The folder data (FolderPrimaryListDto)
 * @returns {Object} Redux action
 */
export const updateFolderData = (spreadsheetId, spreadsheetType, folder) => {
    if (spreadsheetId == null || spreadsheetId === undefined) {
        throw new Error('spreadsheetId is required');
    }
    if (!spreadsheetType) {
        throw new Error('spreadsheetType is required');
    }
    
    return {
        type: SpreadsheetFormatActions.UPDATE_FOLDER_DATA,
        payload: {
            spreadsheetId: Number(spreadsheetId),
            spreadsheetType: spreadsheetType,
            folder
        }
    };
};

/**
 * Action creator to store filter data for a spreadsheet ID
 * @param {number} spreadsheetId - The spreadsheet ID
 * @param {string} spreadsheetType - The spreadsheet type ("Individual" or "Pedigree")
 * @param {Object} filter - The filter data { whereClauses: [...], andOR?: number }
 * @returns {Object} Redux action
 */
export const storeFilterData = (spreadsheetId, spreadsheetType, filter) => {
    if (spreadsheetId == null || spreadsheetId === undefined) {
        throw new Error('spreadsheetId is required');
    }
    if (!spreadsheetType) {
        throw new Error('spreadsheetType is required');
    }
    
    const normalizedFilter = filter === undefined
        ? undefined
        : (filter || { whereClauses: [] });
    
    return {
        type: SpreadsheetFormatActions.STORE_FILTER_DATA,
        payload: {
            spreadsheetId: Number(spreadsheetId),
            spreadsheetType: spreadsheetType,
            filter: normalizedFilter
        }
    };
};

/**
 * Action creator to update filter data for a spreadsheet ID
 * @param {number} spreadsheetId - The spreadsheet ID
 * @param {string} spreadsheetType - The spreadsheet type ("Individual" or "Pedigree")
 * @param {Object} filter - The filter data { whereClauses: [...], andOR?: number }
 * @returns {Object} Redux action
 */
export const updateFilterData = (spreadsheetId, spreadsheetType, filter) => {
    if (spreadsheetId == null || spreadsheetId === undefined) {
        throw new Error('spreadsheetId is required');
    }
    if (!spreadsheetType) {
        throw new Error('spreadsheetType is required');
    }
    
    return {
        type: SpreadsheetFormatActions.UPDATE_FILTER_DATA,
        payload: {
            spreadsheetId: Number(spreadsheetId),
            spreadsheetType: spreadsheetType,
            filter: filter || { whereClauses: [] }
        }
    };
};

